import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Skeleton } from '@/app/components/ui/skeleton';
import { KitForm } from '@/app/components/kits/kit-form';
import { useKit, useUpdateKit, useAddAssetsToKit, useRemoveAssetsFromKit } from '@/hooks/use-kits';
import { toast } from '@/app/components/ui/simple-toaster';
import { PageBreadcrumb } from '@/app/components/page-breadcrumb';

export function EditKitPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const kitId = parseInt(id || '0', 10);

  const { data: kit, isLoading, isError } = useKit(kitId);
  const updateMutation = useUpdateKit();
  const addAssetsMutation = useAddAssetsToKit();
  const removeAssetsMutation = useRemoveAssetsFromKit();

  const handleSubmit = async (data: { name: string; description?: string; assets: number[] }) => {
    if (!kit) return;

    try {
      await updateMutation.mutateAsync({
        id: kitId,
        data: {
          name: data.name,
          description: data.description,
        },
      });

      const currentAssetIds = new Set(kit.assets);
      const newAssetIds = new Set(data.assets);

      const assetsToAdd = data.assets.filter((id) => !currentAssetIds.has(id));
      const assetsToRemove = kit.assets.filter((id) => !newAssetIds.has(id));

      if (assetsToAdd.length > 0) {
        await addAssetsMutation.mutateAsync({
          id: kitId,
          assetIds: assetsToAdd,
        });
      }

      if (assetsToRemove.length > 0) {
        await removeAssetsMutation.mutateAsync({
          id: kitId,
          assetIds: assetsToRemove,
        });
      }

      toast.success('Kit updated successfully');
      navigate(`/kits/${kitId}`);
    } catch {
      toast.error('Failed to update kit');
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !kit) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="text-center py-12">
          <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Kit not found</h2>
          <p className="text-gray-600 mt-2">The kit you're looking for doesn't exist or you don't have access.</p>
          <Button asChild className="mt-4">
            <Link to="/kits">Back to Kits</Link>
          </Button>
        </div>
      </div>
    );
  }

  const isSubmitting =
    updateMutation.isPending || addAssetsMutation.isPending || removeAssetsMutation.isPending;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageBreadcrumb items={[
        { label: 'Kits', href: '/kits' },
        { label: kit.name, href: `/kits/${kitId}` },
        { label: 'Edit' },
      ]} />
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/kits/${kitId}`}>
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Edit Kit</h1>
          <p className="text-gray-600">Update kit details and assets</p>
        </div>
      </div>

      <KitForm
        kit={kit}
        onSubmit={handleSubmit}
        onCancel={() => navigate(`/kits/${kitId}`)}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
